/**
 * Trade-row timestamps for the positions / history ledger.
 *
 * A bare `HH:MM:SS` is useless the moment a trade is older than today — it
 * reads as "this morning" whichever day it happened on — so the clock label
 * carries the date as soon as the trade falls outside today, and the column
 * can be flipped to a relative "x ago" label instead (see `useTimeFormat`).
 */

/** How the Time column renders: wall clock (+ date) or "x ago". */
export type TimeFormat = 'clock' | 'relative';

// Fixed rather than `toLocaleDateString`: en-GB renders September as "Sept",
// and a month that is sometimes four characters wide shifts a tabular column.
const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** ms epoch for any of the shapes trades carry, or null when unparseable. */
export function timestampMs(
    input: number | string | Date | null,
): number | null {
    if (input === null) return null;
    const ms =
        input instanceof Date ? input.getTime() : new Date(input).getTime();
    return Number.isNaN(ms) ? null : ms;
}

function isSameDay(a: Date, b: Date): boolean {
    return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
}

/**
 * `14:03:22` for a trade opened today, `11 Sep 14:03` earlier this year,
 * `11 Sep 25 14:03` before that — the date only shows up when it disambiguates.
 */
export function formatClock(
    at: number | null,
    now: number = Date.now(),
): string {
    if (at === null) return '—';
    const date = new Date(at);
    const today = new Date(now);
    if (isSameDay(date, today)) {
        return date.toLocaleTimeString('en-GB', { hour12: false });
    }
    const day = `${String(date.getDate()).padStart(2, '0')} ${MONTHS[date.getMonth()]}`;
    const year =
        date.getFullYear() === today.getFullYear()
            ? ''
            : ` ${String(date.getFullYear() % 100).padStart(2, '0')}`;
    const time = date.toLocaleTimeString('en-GB', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
    });
    return `${day}${year} ${time}`;
}

/**
 * "4m ago" / "3h ago" / "2d ago". `short` drops the suffix ("4m", "3h") for
 * the compact rows in the trade rail, where the column has no header to
 * explain itself.
 */
export function formatRelative(
    at: number | null,
    { now = Date.now(), short = false }: { now?: number; short?: boolean } = {},
): string {
    if (at === null) return '—';
    // A clock a few seconds ahead of the server must not read "in 3 seconds".
    const diff = Math.max(0, now - at);
    if (diff < MINUTE) return short ? 'now' : 'just now';
    const suffix = short ? '' : ' ago';
    if (diff < HOUR) return `${Math.floor(diff / MINUTE)}m${suffix}`;
    if (diff < DAY) return `${Math.floor(diff / HOUR)}h${suffix}`;
    const days = Math.floor(diff / DAY);
    if (days < 365) return `${days}d${suffix}`;
    return `${Math.floor(days / 365)}y${suffix}`;
}

/** The Time cell's label for the column's current mode. */
export function formatRowTime(
    at: number | null,
    format: TimeFormat,
    now: number = Date.now(),
): string {
    return format === 'relative'
        ? formatRelative(at, { now })
        : formatClock(at, now);
}

/** Full date + time, for the cell's tooltip — always unambiguous. */
export function formatTimestampTitle(at: number | null): string {
    if (at === null) return 'Unknown time';
    return new Date(at).toLocaleString('en-GB', { hour12: false });
}
